/**
 * Chat Message Component
 *
 * Renders a single chat message with support for markdown and embedded cards.
 */

import React from 'react';
import { Box, Typography, Paper, Skeleton, IconButton, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { AIChatMessage } from '../../../types/ai-chat.types';
import { dc } from '../design-tokens';
import ChatCard from './chat-card';

interface ChatMessageProps {
  message: AIChatMessage;
  isLast?: boolean;
  onRetry?: () => void;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, isLast, onRetry }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const [copied, setCopied] = React.useState(false);
  const [showDetails, setShowDetails] = React.useState(false);

  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const isStreaming = message.isStreaming;

  const handleCopy = () => {
    navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat('default', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <Box className={cx(classes.messageContainer, isUser ? 'user' : 'assistant')}>
      {/* Message Content */}
      <Box className={classes.messageContent}>
        {!isUser && (
          <Box className={classes.messageHeader}>
            <Box className={classes.assistantAvatar}>S</Box>
            <Typography variant="caption" className={classes.roleName}>
              {t('paiAssistant', 'SAIA')}
            </Typography>
            <Typography variant="caption" className={classes.timestamp}>
              {formatTime(message.timestamp)}
            </Typography>
          </Box>
        )}

        <Paper className={cx(classes.messageBubble, isUser ? 'user' : 'assistant')} elevation={0}>
          {isStreaming && !message.content ? (
            <Box className={classes.loadingContent}>
              <Skeleton variant="text" width="100%" />
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="text" width="60%" />
            </Box>
          ) : (
            <>
              <Box className={classes.markdownContent}>
                {isStreaming ? (
                  // During streaming, render plain text with blinking cursor for better performance
                  <Typography variant="body2" component="div" className={classes.streamingText}>
                    {message.content}
                    <span className={classes.blinkingCursor}>▊</span>
                  </Typography>
                ) : (
                  // After streaming done, render with full markdown
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => (
                        <Typography variant="body2" component="p" className={classes.paragraph}>
                          {children}
                        </Typography>
                      ),
                      ul: ({ children }) => <ul className={classes.list}>{children}</ul>,
                      ol: ({ children }) => <ol className={classes.list}>{children}</ol>,
                      li: ({ children }) => (
                        <li className={classes.listItem}>
                          <Typography variant="body2" component="span">
                            {children}
                          </Typography>
                        </li>
                      ),
                      code: ({ children, className }) => {
                        const isInline = !className;
                        return isInline ? (
                          <code className={classes.inlineCode}>{children}</code>
                        ) : (
                          <pre className={classes.codeBlock}>
                            <code>{children}</code>
                          </pre>
                        );
                      },
                      strong: ({ children }) => <strong className={classes.strong}>{children}</strong>,
                      a: ({ href, children }) => (
                        <a href={href} target="_blank" rel="noopener noreferrer" className={classes.link}>
                          {children}
                        </a>
                      ),
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                )}
              </Box>

              {/* Embedded Cards */}
              {message.cards && message.cards.length > 0 && (
                <Box className={classes.cardsContainer}>
                  {message.cards.map((card, index) => (
                    <ChatCard key={`${message.id}-card-${index}`} card={card} />
                  ))}
                </Box>
              )}

              {isAssistant && !isStreaming && (message.executionMeta?.steps?.length ?? 0) > 0 && (
                <Box className={classes.executionMetaContainer}>
                  <Typography
                    variant="caption"
                    className={classes.detailsToggle}
                    onClick={() => setShowDetails((prev) => !prev)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setShowDetails((prev) => !prev);
                      }
                    }}
                  >
                    {showDetails
                      ? t('chatHideSteps', 'Hide steps')
                      : t('chatShowSteps', 'Show steps')}
                  </Typography>
                  {showDetails && (
                    <ul className={classes.executionList}>
                      {message.executionMeta!.steps!.map((step, idx) => (
                        <li key={`${message.id}-step-${idx}`}>
                          {step.execute}
                          {step.status === 'error' ? ' ⚠️' : ''}
                        </li>
                      ))}
                    </ul>
                  )}
                </Box>
              )}
            </>
          )}

          {/* Streaming indicator */}
          {isStreaming && message.content && (
            <Box className={classes.streamingIndicator}>
              <span className={classes.typingDot} />
              <span className={classes.typingDot} />
              <span className={classes.typingDot} />
            </Box>
          )}
        </Paper>

        {/* Message Actions */}
        {isAssistant && !isStreaming && (
          <Box className={classes.messageActions}>
            <Tooltip title={copied ? t('copied', 'Copied!') : t('copyToClipboard', 'Copy')}>
              <IconButton size="small" onClick={handleCopy} className={classes.actionButton}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {isLast && onRetry && (
              <Tooltip title={t('regenerate', 'Regenerate response')}>
                <IconButton size="small" onClick={onRetry} className={classes.actionButton}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  messageContainer: {
    display: 'flex',
    marginBottom: theme.spacing(2.5),
    '&.user': {
      justifyContent: 'flex-end',
    },
    '&.assistant': {
      justifyContent: 'flex-start',
    },
  },
  assistantAvatar: {
    width: 22,
    height: 22,
    borderRadius: 7,
    background: dc.gradient,
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
  },
  messageContent: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '92%',
    minWidth: 0,
    '$messageContainer.user &': {
      maxWidth: '86%',
      alignItems: 'flex-end',
    },
  },
  messageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.1),
    marginBottom: theme.spacing(1),
  },
  roleName: {
    fontWeight: 700,
    color: dc.muted,
    fontSize: '0.75rem',
  },
  timestamp: {
    color: dc.faint,
    fontSize: '0.65rem',
  },
  messageBubble: {
    '&.user': {
      backgroundColor: dc.bubbleBg,
      border: `1px solid ${dc.bubbleBorder}`,
      color: dc.ink,
      borderRadius: '20px 20px 6px 20px',
      padding: theme.spacing(1.4, 2),
      fontSize: 15,
      lineHeight: 1.6,
      '& a': {
        color: dc.primary,
        textDecoration: 'underline',
      },
    },
    '&.assistant': {
      backgroundColor: 'transparent',
      color: dc.ink,
      border: 'none',
      padding: 0,
      paddingLeft: 33,
      fontSize: 15,
      lineHeight: 1.72,
    },
  },
  loadingContent: {
    width: 200,
  },
  markdownContent: {
    '& > *:first-of-type': {
      marginTop: 0,
    },
    '& > *:last-child': {
      marginBottom: 0,
    },
  },
  paragraph: {
    marginBottom: theme.spacing(1),
    lineHeight: 1.6,
    '&:last-child': {
      marginBottom: 0,
    },
  },
  list: {
    margin: theme.spacing(0.5, 0),
    paddingLeft: theme.spacing(2.5),
  },
  listItem: {
    marginBottom: theme.spacing(0.5),
  },
  inlineCode: {
    backgroundColor: theme.palette.greys.lightest,
    padding: '2px 6px',
    borderRadius: 4,
    fontFamily: 'monospace',
    fontSize: '0.85em',
    color: theme.palette.primary.dark,
  },
  codeBlock: {
    backgroundColor: theme.palette.greys.lightest,
    padding: theme.spacing(1.5),
    borderRadius: 8,
    overflow: 'auto',
    fontFamily: 'monospace',
    fontSize: '0.85em',
    margin: theme.spacing(1, 0),
    border: `1px solid ${theme.palette.greys.lighter}`,
    color: theme.palette.text.primary,
  },
  strong: {
    fontWeight: 600,
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  cardsContainer: {
    marginTop: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  detailsToggle: {
    cursor: 'pointer',
    color: theme.palette.text.secondary,
    textDecoration: 'underline',
    textDecorationStyle: 'dotted',
    '&:hover': {
      color: theme.palette.text.primary,
    },
  },
  executionMetaContainer: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: 8,
    backgroundColor: theme.palette.greys.lightest,
    border: `1px solid ${theme.palette.greys.lighter}`,
  },
  executionPhase: {
    display: 'block',
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  executionBlock: {
    marginTop: theme.spacing(0.5),
  },
  executionTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  executionList: {
    margin: theme.spacing(0.25, 0, 0, 2),
    padding: 0,
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  streamingText: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    lineHeight: 1.6,
  },
  blinkingCursor: {
    display: 'inline',
    animation: '$blink 1s infinite',
    marginLeft: 2,
    color: theme.palette.primary.light,
  },
  '@keyframes blink': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0 },
    '100%': { opacity: 1 },
  },
  streamingIndicator: {
    display: 'flex',
    gap: 4,
    marginTop: theme.spacing(1),
  },
  typingDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.light,
    animation: '$typingBounce 1.4s infinite ease-in-out both',
    '&:nth-of-type(1)': {
      animationDelay: '-0.32s',
    },
    '&:nth-of-type(2)': {
      animationDelay: '-0.16s',
    },
  },
  '@keyframes typingBounce': {
    '0%, 80%, 100%': {
      transform: 'scale(0)',
    },
    '40%': {
      transform: 'scale(1)',
    },
  },
  messageActions: {
    display: 'flex',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    opacity: 0,
    transition: 'opacity 0.2s',
    '$messageContainer:hover &': {
      opacity: 1,
    },
  },
  actionButton: {
    padding: 4,
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.primary.main,
    },
    '& svg': {
      fontSize: '0.875rem',
    },
  },
}));

export default ChatMessage;

